import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { SITE_URL } from "@/lib/siteConfig";

const TITLE = "Model card | Myelina Health";
const DESC =
  "A plain-English description of Myelina's energy-pattern engine: what it uses, what it doesn't, when it should be ignored, and how we keep it honest.";

const ModelCard = () => {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/model-card`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-3xl">
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">Model card</h1>
        <p className="lead-italic text-foreground/80 mb-12">
          What Myelina's energy-pattern engine does, and, just as important, what it doesn't.
        </p>

        <section className="space-y-10 text-[16px] leading-[1.8] text-foreground/85">
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What it does</h2>
            <p>
              Myelina reads your own check-ins, reflections, and (if you connect one) your
              wearable to surface patterns in your energy that you can plan around, a likely
              afternoon window to protect, a habit that tends to move with better mornings.
              It observes; it does not diagnose.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What it uses</h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Your daily energy check-ins and evening reflections.</li>
              <li>Medications you choose to log (name, dose, timing).</li>
              <li>Optional wearable imports: sleep, HRV, step count.</li>
              <li>Optional weather signal for your saved location.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What it doesn't use</h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Any other user's data. Patterns are personal, not population-wide.</li>
              <li>Insurance, employment, or credit signals.</li>
              <li>Third-party ad or tracking profiles.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">How we keep it honest</h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>60-day minimum.</strong> Correlations only surface after ~60 days of your
                own data, so a good week can't masquerade as a pattern.
              </li>
              <li>
                <strong>Plain-language confidence.</strong> We show a 95% confidence range around
                every correlation, and describe it in English (e.g. "somewhere between a small
                and a moderate link").
              </li>
              <li>
                <strong>Multiple-testing correction.</strong> When we test many possible patterns
                at once we apply the Benjamini–Hochberg false-discovery-rate correction, so a
                handful of coincidences don't get reported as real.
              </li>
              <li>
                <strong>Illustrative mockups are labelled.</strong> Any sample energy forecast on
                the marketing site is marked "Illustrative only" and does not come from your data.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">When to ignore it</h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>New or changing symptoms, call your neurology team, not Myelina.</li>
              <li>
                Medication decisions of any kind. Myelina will never suggest deferring an
                appointment, a dose, or a prescription refill.
              </li>
              <li>
                A day that doesn't feel like your data. Your body is the ground truth; the model
                is a second opinion at best.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Citations</h2>
            <p>The engine's assumptions draw on published MS-fatigue research, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>National MS Society, prevalence and symptom prevalence data (NMSS, 2024).</li>
              <li>
                Kratz AL et al. (2017), day-to-day variability of fatigue in multiple sclerosis.
              </li>
              <li>
                Blikman LJ et al., energy conservation management for MS-related fatigue.
              </li>
              <li>Chen MH et al. (2025), wearable signals and fatigue prediction in MS.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Questions or corrections</h2>
            <p>
              Email <a className="underline" href="mailto:hello@myelinahealth.com">hello@myelinahealth.com</a>.
              We update this page whenever the engine's rules change and log the change in the{" "}
              <a className="underline" href="/changelog">changelog</a>.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ModelCard;
